import { useState, useEffect } from 'react';
import {
  User,
  Key,
  Mail,
  Globe,
  Save,
  CheckCircle2,
  Zap,
  Loader2
} from 'lucide-react';

import api from '../api';

export default function Settings() {
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState({
    fullName: localStorage.getItem('settings_fullName') || 'John Doe',
    email: localStorage.getItem('settings_email') || 'john@example.com',
    googleApiKey: localStorage.getItem('settings_googleApiKey') || '',
    groqApiKey: localStorage.getItem('settings_groqApiKey') || '',
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPassword: '',
    emailSignature: ''
  });

  useEffect(() => {
    api.get('/settings/smtp')
      .then(res => {
        if (res.data) {
          setSettings(prev => ({
            ...prev,
            smtpHost: res.data.smtpHost || '',
            smtpPort: res.data.smtpPort || '587',
            smtpUser: res.data.smtpUser || '',
            smtpPassword: res.data.hasPassword ? '********' : '',
            emailSignature: res.data.emailSignature || ''
          }));
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to load SMTP settings", err);
        setLoading(false);
      });
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    localStorage.setItem('settings_fullName', settings.fullName);
    localStorage.setItem('settings_email', settings.email);
    localStorage.setItem('settings_googleApiKey', settings.googleApiKey);
    localStorage.setItem('settings_groqApiKey', settings.groqApiKey);

    try {
      await api.post('/settings/smtp', {
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        smtpUser: settings.smtpUser,
        smtpPassword: settings.smtpPassword,
        emailSignature: settings.emailSignature
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert("Failed to save SMTP settings");
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/settings/smtp/verify', {
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        smtpUser: settings.smtpUser,
        smtpPassword: settings.smtpPassword,
        emailSignature: settings.emailSignature
      });
      setTestResult({ status: 'success', message: res.data.message });
    } catch (err) {
      setTestResult({ status: 'error', message: err.response?.data?.detail || "Failed to verify connection" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading settings...</div>;

  return (
    <div className="p-8 max-w-4xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-slate-400">Manage your account and API configurations</p>
      </div>

      <div className="space-y-6">
        <section className="glass-card rounded-2xl overflow-hidden border border-white/[0.06]">
          <div className="p-6 border-b border-white/[0.06] flex items-center gap-3">
            <User className="text-indigo-400" size={20} />
            <h3 className="font-semibold text-lg text-white">Profile Information</h3>
          </div>
          <div className="p-6 grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Full Name</label>
              <input type="text" value={settings.fullName} onChange={(e) => handleChange('fullName', e.target.value)} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Email Address</label>
              <input type="email" value={settings.email} onChange={(e) => handleChange('email', e.target.value)} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
            </div>
          </div>
        </section>

        <section className="glass-card rounded-2xl overflow-hidden border border-white/[0.06]">
          <div className="p-6 border-b border-white/[0.06] flex items-center gap-3">
            <Key className="text-indigo-400" size={20} />
            <h3 className="font-semibold text-lg text-white">API Configuration</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-sm text-slate-400 flex items-center gap-2">
                <Globe size={14} /> Google Maps API Key
              </label>
              <input type="password" value={settings.googleApiKey} onChange={(e) => handleChange('googleApiKey', e.target.value)} placeholder="AIza..." className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-400 flex items-center gap-2">
                <Zap size={14} /> GROQ API Key
              </label>
              <input type="password" value={settings.groqApiKey} onChange={(e) => handleChange('groqApiKey', e.target.value)} placeholder="gsk_..." className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
            </div>
          </div>
        </section>

        <section className="glass-card rounded-2xl overflow-hidden border border-white/[0.06]">
          <div className="p-6 border-b border-white/[0.06] flex items-center gap-3">
            <Mail className="text-indigo-400" size={20} />
            <h3 className="font-semibold text-lg text-white">Email Settings (SMTP)</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">SMTP Host</label>
                <input type="text" value={settings.smtpHost} onChange={(e) => handleChange('smtpHost', e.target.value)} placeholder="smtp.gmail.com" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">SMTP Port</label>
                <input type="number" value={settings.smtpPort} onChange={(e) => handleChange('smtpPort', e.target.value)} placeholder="587" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">SMTP Username (Email)</label>
                <input type="text" value={settings.smtpUser} onChange={(e) => handleChange('smtpUser', e.target.value)} placeholder="you@company.com" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">SMTP Password (App Password)</label>
                <input type="password" value={settings.smtpPassword} onChange={(e) => handleChange('smtpPassword', e.target.value)} placeholder="********" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 text-white" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Email Signature</label>
              <textarea
                value={settings.emailSignature}
                onChange={(e) => handleChange('emailSignature', e.target.value)}
                placeholder="Best Regards,&#10;John Doe"
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 outline-none focus:border-indigo-500/50 h-24 resize-none text-white"
              />
            </div>

            <div className="flex items-center justify-between border-t border-white/[0.06] pt-6">
              <div>
                {testResult && (
                  <span className={`text-sm font-medium ${testResult.status === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {testResult.message}
                  </span>
                )}
              </div>
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="px-6 py-2 bg-white/[0.05] hover:bg-white/[0.08] text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {testing ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                Test Connection
              </button>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-4">
          <button className="px-6 py-2 rounded-xl font-medium text-slate-300 hover:bg-white/[0.05] transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all active:scale-95"
          >
            {saved ? <CheckCircle2 size={18} /> : <Save size={18} />}
            {saved ? 'Settings Saved' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
